import { makeAutoObservable } from "mobx";
import axios, { AxiosInstance } from "axios";
import { history } from "routes";
import { getEnv } from "mobx-easy";
import { RootEnv } from "stores/setup/create-store";
import { IAuthProfilePayload } from "../Auth/AuthModel";
import { isEmbeddedMode, getEmbeddedToken, waitForEmbeddedToken, refreshEmbeddedToken } from "util/embedded";

export interface TokenSet {
  token?: string;
  refreshToken?: string;
}

export const TOKEN_KEY = "kcs_tcd_dkt";

export class SecurityStore {
  tokenSet: TokenSet | null = null;
  private loading: boolean = false;

  constructor(apis: AxiosInstance[] = []) {
    makeAutoObservable(this);

    apis.forEach((api) => {
      // Authenticate all the requests automatically
      // when an access token is available
      api.interceptors.request.use(async (config) => {
        // In embedded mode, use token received from parent app via postMessage
        if (isEmbeddedMode()) {
          // Wait for token if not yet received (handles race condition during initial load)
          let embeddedToken = getEmbeddedToken();

          if (!embeddedToken) {
            try {
              // Wait up to 10 seconds for token from parent app
              embeddedToken = await Promise.race([
                waitForEmbeddedToken(),
                new Promise<string>((_, reject) =>
                  setTimeout(() => reject(new Error('Token timeout')), 10000)
                ),
              ]);
            } catch (error) {
              console.error('[EmbeddedAuth] Timeout waiting for token from parent app');
            }
          }

          if (embeddedToken && config.headers) {
            //@ts-ignore
            config.headers.Authorization = `Bearer ${embeddedToken}`;
          }
          return config;
        }

        // Normal mode: add token from localStorage
        if (config.headers && this.tokenSet?.token) {
          //@ts-ignore
          config.headers.Authorization = `Bearer ${this.tokenSet.token}`;
        }
        return config;
      });

      api.interceptors.response.use(undefined, async (error) => {
        const originalRequest = error.config;
        const { logServices } = getEnv<RootEnv>();

        if (error?.response?.status === 500 || error?.code === "ECONNABORTED") {
          history.push("/500");
          return Promise.reject(error);
        }

        if (error.message === "Request cancelled") {
          return Promise.reject(error);
        }

        // In embedded mode, handle 401 with token refresh and retry
        if (isEmbeddedMode()) {
          if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              // Request fresh token from parent and wait for it
              const newToken = await refreshEmbeddedToken();

              // Update the request with new token and retry
              if (newToken && originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              return api(originalRequest);
            } catch (refreshError) {
              console.error("[EmbeddedAuth] Failed to refresh token:", refreshError);
              return Promise.reject(error);
            }
          }
          return Promise.reject(error);
        }

        // Normal mode: handle 401 with token refresh
        if (
          error.response?.status === 401 &&
          error.response?.data?.name === "REFRESH_TOKEN_ERROR"
        ) {
          this.clearTokenSet();
          window.location.href = "/login";
        } else if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (!this.tokenSet?.refreshToken) {
            console.log("No refresh token available");
            this.clearTokenSet();
          }

          const response = await api.post("/v1/auth/refreshToken", {
            refreshToken: this.tokenSet?.refreshToken,
          });
          const { accessToken } = response.data;

          this.setTokenSet({
            token: accessToken,
            refreshToken: this.tokenSet?.refreshToken,
          });
          axios.defaults.headers.common.Authorization = "Bearer " + accessToken;
          return api(originalRequest);
        }

        return Promise.reject(error);
      });
    });
  }

  private setLoading(value: boolean) {
    this.loading = value;
  }

  public isLoading(): boolean {
    return this.loading;
  }

  clearTokenSet() {
    this.tokenSet = null;
    localStorage.removeItem(TOKEN_KEY);
  }

  setTokenSet(tokenSet: TokenSet) {
    this.tokenSet = tokenSet;
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenSet));
  }

  isAuthorized() {
    // In embedded mode, always return true to prevent redirect to login
    // The actual authentication is handled by:
    // 1. Parent app (i4cast-web v2) which manages the Clerk session
    // 2. Request interceptor which waits for token via postMessage before API calls
    // This prevents the race condition where isAuthorized() is checked before token arrives
    if (isEmbeddedMode()) {
      return true;
    }

    // Normal mode: check localStorage for token
    return localStorage.getItem(TOKEN_KEY) !== null;
  }

  async getTokenSet(): Promise<void> {
    this.setLoading(true);
    try {
      const tokenSet = localStorage.getItem(TOKEN_KEY);

      this.tokenSet = tokenSet && JSON.parse(tokenSet);
    } catch (e) {
      throw e;
    } finally {
      this.setLoading(false);
    }
  }
}
